import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import SeoHead from "@/components/feature/SeoHead";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

function HeroSection() {
  return (
    <section
      className="relative w-full h-[520px] md:h-[680px] flex items-center justify-center overflow-hidden"
      style={{
        backgroundImage:
          "url(https://readdy.ai/api/search-image?query=Couple%20sitting%20together%20in%20a%20cozy%20home%20cinema%20room%20watching%20movie%20on%20large%20screen%20warm%20dim%20lighting%20romantic%20atmosphere%20soft%20fairy%20lights%20textured%20throw%20blankets%20cinematic%20cozy%20aesthetic%20intimate%20scene&width=1920&height=1080&seq=hero-couple-cinema&orientation=landscape)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/70"></div>
      <div className="relative z-10 w-full px-4 md:px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 md:mb-6 leading-tight">
            夫妻影院
            <span className="block text-xl md:text-2xl lg:text-3xl font-body font-normal mt-3 text-white/90">
              二人世界的浪漫电影之旅
            </span>
          </h1>
          <p className="text-base md:text-lg text-white/80 mb-8 md:mb-10 max-w-2xl mx-auto leading-relaxed">
            精选适合夫妻情侣共同观看的浪漫电影推荐，免费高清在线观看不缓冲，最新夫妻必看影视高清全集，为您打造温馨难忘的二人世界观影体验
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/category/latest"
              className="px-8 py-3 rounded-full bg-primary-500 text-white font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              <i className="ri-fire-line mr-2"></i>
              最新推荐
            </Link>
            <Link
              to="/category/romance"
              className="px-8 py-3 rounded-full bg-white/20 backdrop-blur-sm text-white font-medium text-sm whitespace-nowrap hover:bg-white/30 transition-colors border border-white/30"
            >
              <i className="ri-heart-line mr-2"></i>
              浪漫爱情
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function CategorySection() {
  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="px-4 md:px-6 max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
            电影分类
          </h2>
          <p className="text-foreground-600 text-sm md:text-base">
            按类型探索适合夫妻情侣共同观看的电影
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="group relative bg-background-50 border border-secondary-200 rounded-xl p-5 md:p-6 flex flex-col items-center text-center transition-all duration-300 hover:border-primary-300 hover:bg-primary-50"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-3 md:mb-4 group-hover:bg-primary-500 group-hover:text-white transition-all duration-300">
                <i className={`${cat.icon} text-xl md:text-2xl`}></i>
              </div>
              <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 mb-1 whitespace-nowrap">
                {cat.name}
              </h3>
              <p className="text-xs text-foreground-600 line-clamp-2">
                {cat.description}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedSection() {
  const featured = articles.slice(0, 4);

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="px-4 md:px-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-10 md:mb-14">
          <div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
              精选推荐
            </h2>
            <p className="text-foreground-600 text-sm">编辑团队为你甄选的夫妻必看电影</p>
          </div>
          <Link
            to="/category/latest"
            className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap"
          >
            查看全部
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {featured.map((article) => (
            <Link
              key={article.id}
              to={`/article/${article.id}`}
              className="group bg-background-50 rounded-xl overflow-hidden border border-secondary-200 transition-all duration-300 hover:border-primary-300"
            >
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={article.thumbnail}
                  alt={article.title}
                  title={`${article.title} - 夫妻影院推荐`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 text-white text-xs">
                  {article.rating}分
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-primary-600 bg-primary-100 px-2 py-0.5 rounded-full">
                    {categories.find((c) => c.slug === article.category)?.name}
                  </span>
                  <span className="text-xs text-foreground-500">{article.year}</span>
                </div>
                <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                  {article.title}
                </h3>
                <p className="text-xs text-foreground-600 line-clamp-2 leading-relaxed">
                  {article.summary}
                </p>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-8 text-center sm:hidden">
          <Link
            to="/category/latest"
            className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap"
          >
            查看全部
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}

function LatestSection() {
  const latest = articles.slice(4);

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="px-4 md:px-6 max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
            最新文章
          </h2>
          <p className="text-foreground-600 text-sm">更多夫妻情侣观影推荐</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {latest.map((article) => (
            <Link
              key={article.id}
              to={`/article/${article.id}`}
              className="group bg-background-50 rounded-xl overflow-hidden border border-secondary-200 transition-all duration-300 hover:border-primary-300"
            >
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={article.thumbnail}
                  alt={article.title}
                  title={`${article.title} - 夫妻影院推荐`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 text-white text-xs">
                  {article.rating}分
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-primary-600 bg-primary-100 px-2 py-0.5 rounded-full">
                    {categories.find((c) => c.slug === article.category)?.name}
                  </span>
                  <span className="text-xs text-foreground-500">{article.year}</span>
                </div>
                <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                  {article.title}
                </h3>
                <p className="text-xs text-foreground-600 line-clamp-2 leading-relaxed">
                  {article.summary}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsSection() {
  const stats = [
    { number: "500+", label: "精选电影", icon: "ri-movie-2-line" },
    { number: "12", label: "电影分类", icon: "ri-price-tag-3-line" },
    { number: "100K+", label: "观影用户", icon: "ri-user-heart-line" },
    { number: "9.2", label: "平均评分", icon: "ri-star-line" },
  ];

  return (
    <section className="py-12 md:py-16 bg-accent-500">
      <div className="px-4 md:px-6 max-w-5xl mx-auto">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-600/30">
                <i className={`${stat.icon} text-xl text-white`}></i>
              </div>
              <div className="text-2xl md:text-3xl font-heading font-bold text-white mb-1">
                {stat.number}
              </div>
              <div className="text-sm text-white/80">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const faqs = [
    {
      q: "夫妻影院是什么？",
      a: "夫妻影院是一个专注于为夫妻情侣推荐适合共同观看电影的内容平台。我们精心挑选浪漫爱情、轻松喜剧、情感剧情、经典重温等各类电影，帮助每一对夫妻情侣找到适合二人世界的观影选择。",
    },
    {
      q: "推荐的电影在哪里可以观看？",
      a: "我们提供详细的电影介绍、剧情分析和观影建议，帮助您了解电影内容后自行选择各大正规平台观看。我们的推荐文章包含电影的基本信息、故事情节和适合的观影场景。",
    },
    {
      q: "如何选择适合夫妻一起看的电影？",
      a: "选择夫妻电影可以从以下几个方面考虑：双方的观影喜好、当前的心情状态、观影时间和氛围。我们建议先从轻松浪漫的类型开始，如《怦然心动》《时空恋旅人》等温暖治愈的作品。",
    },
    {
      q: "夫妻影院多久更新一次内容？",
      a: "我们的编辑团队每周都会更新最新的电影推荐文章，确保您能获得最新、最优质的夫妻电影推荐内容。",
    },
  ];

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="px-4 md:px-6 max-w-3xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
            常见问题
          </h2>
          <p className="text-foreground-600 text-sm">关于夫妻影院的常见疑问解答</p>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <details
              key={index}
              className="group bg-background-50 rounded-xl border border-secondary-200 overflow-hidden"
            >
              <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                <span className="font-medium text-sm md:text-base text-foreground-950 pr-4">
                  {faq.q}
                </span>
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-add-line text-foreground-500 group-open:hidden"></i>
                  <i className="ri-subtract-line text-foreground-500 hidden group-open:block"></i>
                </div>
              </summary>
              <div className="px-5 pb-5 text-sm text-foreground-700 leading-relaxed">
                {faq.a}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [statusMessage, setStatusMessage] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value;
    if (honeypot && honeypot.trim() !== "") {
      setStatus("success");
      setStatusMessage("订阅成功！感谢您的关注。");
      return;
    }
    try {
      const formData = new FormData(form);
      const response = await fetch("https://readdy.ai/api/form/d9njda8bdkpk99j7gkmg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      if (response.ok) {
        setStatus("success");
        setStatusMessage("订阅成功！感谢您的关注。");
        setEmail("");
      } else {
        setStatus("error");
        setStatusMessage("订阅失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setStatusMessage("网络错误，请稍后重试。");
    }
  };

  return (
    <section className="py-14 md:py-20 bg-primary-500">
      <div className="px-4 md:px-6 max-w-2xl mx-auto text-center">
        <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
          订阅我们的推荐
        </h2>
        <p className="text-white/80 text-sm mb-8">
          每周获取最新夫妻电影推荐，不错过任何一部好片
        </p>
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          data-readdy-form="true"
        >
          <input
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="spam-field"
          />
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="请输入您的邮箱"
            required
            className="flex-1 px-5 py-3 rounded-full bg-white text-foreground-950 placeholder-foreground-400 text-sm focus:outline-none focus:ring-2 focus:ring-white/40"
          />
          <button
            type="submit"
            className="px-6 py-3 rounded-full bg-accent-500 text-white font-medium text-sm whitespace-nowrap hover:bg-accent-600 transition-colors"
          >
            立即订阅
          </button>
        </form>
        {status === "success" && (
          <p className="mt-4 text-white/90 text-sm">{statusMessage}</p>
        )}
        {status === "error" && (
          <p className="mt-4 text-accent-200 text-sm">{statusMessage}</p>
        )}
      </div>
    </section>
  );
}

export default function Home() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "夫妻影院 - 夫妻电影与情侣影院精选",
    description:
      "夫妻影院提供适合夫妻情侣共同观看的浪漫电影推荐，二人世界电影，免费高清在线观看不缓冲，最新夫妻必看影视高清全集",
    url: "https://fuqicinema.com/",
    isPartOf: {
      "@type": "WebSite",
      name: "夫妻影院",
      url: "https://fuqicinema.com",
    },
  };

  return (
    <>
      <SeoHead
        title="夫妻影院 - 夫妻电影与情侣影院精选"
        description="夫妻影院提供适合夫妻情侣共同观看的浪漫电影推荐，二人世界电影，免费高清在线观看不缓冲，最新夫妻必看影视高清全集，温馨观影体验。"
        keywords="夫妻影院,夫妻电影,情侣影院,二人世界电影,浪漫电影推荐,免费高清,在线观看不缓冲,夫妻必看影视"
        canonicalUrl="https://fuqicinema.com/"
        ogType="website"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homeSchema) }}
      />
      <Navbar />
      <main>
        <HeroSection />
        <CategorySection />
        <StatsSection />
        <FeaturedSection />
        <LatestSection />
        <FaqSection />
        <NewsletterSection />
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}